"use client";

import { useMemo } from "react";

import { Icons } from "@workspace/ui/components/icons";
import { Marquee } from "@workspace/ui/components/marquee";

import {
  Section,
  SectionHeader,
  SectionHeading,
  SectionHeadingBadge,
  SectionHeadingDescription,
  SectionHeadingTitle,
} from "~/components/common/layout/section";
import { TurboLink } from "~/components/common/turbo-link";
import { appConfig } from "~/config/app";
import { pathsConfig } from "~/config/paths";
import { Product } from "~/lib/constants/products";
import { useActiveProduct } from "~/lib/hooks/use-active-product";

import { PROJECTS } from "./constants/projects";
import { Project } from "./project";

function sortProjectsByActiveProduct(
  projects: readonly (typeof PROJECTS)[number][],
  activeProduct: Product,
): (typeof PROJECTS)[number][] {
  return [...projects].sort((a, b) => {
    const aMatches = a.products.some((p) => p === activeProduct) ? 0 : 1;
    const bMatches = b.products.some((p) => p === activeProduct) ? 0 : 1;
    return aMatches - bMatches;
  });
}

export const ProjectsMarquee = () => {
  const product = useActiveProduct();
  const orderedProjects = useMemo(
    () => sortProjectsByActiveProduct(PROJECTS, product),
    [product],
  );
  const total = orderedProjects.length;
  const rowSize = Math.ceil(total / 3);

  const rows = [
    orderedProjects.slice(0, rowSize),
    orderedProjects.slice(rowSize, rowSize * 2),
    orderedProjects.slice(rowSize * 2),
  ];

  const marqueeDirections = ["normal", "reverse", "normal"];

  return (
    <div className="relative col-start-2 -mx-6 flex w-[calc(100%+3rem)] flex-col items-center justify-center overflow-hidden rounded-lg sm:-mx-8 sm:w-[calc(100%+4rem)] xl:-mx-0 xl:w-full">
      {rows.map((row, i) => (
        <Marquee
          key={i}
          pauseOnHover
          className="[--duration:60s]"
          reverse={marqueeDirections[i] === "reverse"}
        >
          {row.map((project) => (
            <Project key={project.name} project={project} />
          ))}
        </Marquee>
      ))}
      <div className="from-background pointer-events-none absolute inset-y-0 left-0 w-1/6 bg-gradient-to-r sm:w-1/5" />
      <div className="from-background pointer-events-none absolute inset-y-0 right-0 w-1/6 bg-gradient-to-l sm:w-1/5" />
    </div>
  );
};

export const Showcase = () => (
  <Section className="!col-span-full grid grid-cols-subgrid" id="showcase">
    <SectionHeader className="col-start-2">
      <SectionHeadingBadge icon={Icons.WebsiteSparkles} text="Showcase" />

      <SectionHeading>
        <SectionHeadingTitle>
          What could you build with {appConfig.name}?
        </SectionHeadingTitle>

        <SectionHeadingDescription>
          Below are some of the SaaS projects that have been built with our
          starter kit.
        </SectionHeadingDescription>

        <TurboLink
          href={pathsConfig.showcase}
          className="text-primary flex items-center gap-1.5 text-sm"
        >
          <Icons.LinkSquare className="size-3.5" strokeWidth={2} /> See all
          projects
        </TurboLink>
      </SectionHeading>
    </SectionHeader>

    <ProjectsMarquee />
  </Section>
);
